<template>
  <div
    ref="container"
    class="message-list flex-grow-1 overflow-y-auto pa-4"
    @scroll="onScroll"
  >
    <!-- Empty state -->
    <div
      v-if="messages.length === 0 && !showSpinner"
      class="d-flex flex-column align-center justify-center fill-height text-medium-emphasis"
    >
      <slot name="empty-state">
        <img :src="emptyStateIcon" alt="RPilot" class="rpilot-empty-icon mb-4" />
        <div class="text-h6 mb-2">RPilot</div>
        <div class="text-body-2">{{ t('chat.startConversation') }}</div>
      </slot>
    </div>

    <template v-for="(item, i) in messages" :key="i">
      <ThinkingBlock
        v-if="item._thinkingBlock"
        :block="item"
        :agent-streaming="agentIsStreaming && i === lastThinkingIdx"
        :status="i === lastThinkingIdx ? activeBlockStatus : ''"
      />
      <!-- Reasoning text is gated by features.thinking AND the show-tool-calls
           toggle. With the toggle off we deliberately render no thought-process
           block — the agent loader's single-line status carries the reasoning
           instead, keeping the view minimal. -->
      <template v-else-if="item._reasoningBlock">
        <ReasoningBlock v-if="showReasoning && chatStore.showToolCalls" :block="item" />
      </template>
      <DocsPageViewer
        v-else-if="item.role === 'docs_page'"
        :page="item.page"
        :url="item.url"
        :content="item.content"
      />
      <!-- Human-in-the-loop tool approval. Always rendered (never filtered by
           the show-tool-calls toggle) since it's a blocking prompt. The batch
           variant folds a whole round of mutations into one card. -->
      <ToolApprovalBatchCard
        v-else-if="item.role === 'tool_approval_batch'"
        :message="item"
      />
      <ToolApprovalCard
        v-else-if="item.role === 'tool_approval'"
        :message="item"
      />
      <!-- Mid-session credit rejection: a calm notice + contact-sales dialog,
           in place of a raw error bubble. -->
      <CreditExhaustedNotice v-else-if="item.role === 'credit_exhausted'" />
      <MessageBubble v-else :message="item" />
      <!-- Thumbs up/down under the last completed assistant turn. Session-level
           rating (PUT /sessions/{id}/rating); hidden while a turn is in flight
           so a half-finished answer can't be rated. -->
      <RatingFooter v-if="showRatingFooter && i === lastTurnIdx" />
    </template>

    <div v-if="showSpinner" class="d-flex align-center mb-3 justify-start">
      <AgentLoader :status="spinnerStatus" />
    </div>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import MessageBubble from './MessageBubble.vue'
import RatingFooter from './RatingFooter.vue'
import ThinkingBlock from './ThinkingBlock.vue'
import ReasoningBlock from './ReasoningBlock.vue'
import DocsPageViewer from './DocsPageViewer.vue'
import ToolApprovalCard from './ToolApprovalCard.vue'
import ToolApprovalBatchCard from './ToolApprovalBatchCard.vue'
import CreditExhaustedNotice from './CreditExhaustedNotice.vue'
import AgentLoader from './AgentLoader.vue'
import { useAutoScroll } from '../../composables/useAutoScroll.js'
import { useChatStore } from '../../stores/chat.js'
import { useTheme } from '../../composables/useTheme.js'
import iconDark from '../../assets/icons/rpilot-icon-dark.svg'
import iconLight from '../../assets/icons/rpilot-icon-light.svg'
import { toRef, computed } from 'vue'

const chatStore = useChatStore()
const { isDark } = useTheme()
const emptyStateIcon = computed(() => (isDark() ? iconDark : iconLight))

const props = defineProps({
  messages: { type: Array, default: () => [] },
  features: { type: Object, default: () => ({}) },
  showSpinner: { type: Boolean, default: false },
  spinnerStatus: { type: String, default: '' },
  // Status for the active ThinkingBlock's header while the turn is paused on
  // a frontend tool (the loader is hidden behind a trailing block when the
  // tool timeline is on, so the header carries the wait message instead).
  activeBlockStatus: { type: String, default: '' },
})

// features.thinking defaults to on; only an explicit `false` hides reasoning.
const showReasoning = computed(() => props.features?.thinking !== false)

// isBusy, not isStreaming: the active block keeps shimmering while the turn is
// paused on a frontend tool (no open stream, but the turn is not over).
const agentIsStreaming = computed(() => chatStore.isBusy)

const { container, onScroll } = useAutoScroll(toRef(props, 'messages'))

const lastThinkingIdx = computed(() => {
  for (let i = props.messages.length - 1; i >= 0; i--) {
    const m = props.messages[i]
    if (!m._thinkingBlock && m.role === 'user') return -1
    if (m._thinkingBlock) return i
  }
  return -1
})

// features.rating defaults to on; only an explicit `false` hides the thumbs.
// Hidden while busy (including a frontend-tool pause) so the thumbs never
// appear under a bubble that is only the first half of the turn.
const showRatingFooter = computed(
  () => props.features?.rating !== false && !chatStore.isBusy
)

// Item that owns the rating footer: the last assistant text or docs_page,
// skipping thinking/reasoning blocks. Anything else at the tail (a user
// message awaiting its reply, a pending tool approval) means there is no
// finished answer to rate.
const lastTurnIdx = computed(() => {
  for (let i = props.messages.length - 1; i >= 0; i--) {
    const m = props.messages[i]
    if (m._thinkingBlock || m._reasoningBlock) continue
    return m.role === 'assistant' || m.role === 'docs_page' ? i : -1
  }
  return -1
})
</script>

<style scoped>
.message-list {
  min-height: 0;
  /* Flex items default min-width to their content, so one unshrinkable child
     (e.g. a long-filename chip) would widen the whole list past the widget. */
  min-width: 0;
}
/* In light mode, use a soft off-white chat backdrop so the white agent
   bubbles read as distinct cards against it. */
.v-theme--light .message-list {
  background-color: #fafafa;
}
.rpilot-empty-icon {
  width: 96px;
  height: 96px;
  display: block;
}
</style>
